import { ArcgisOptions, GeoJsonSourceOptions } from "@areahub/arcgis";
import { WildfireDataFormatter } from "@areahub/common";
import { colors } from "@areahub/theme";
import { FeatureServiceLayerDefinition } from "@ui/maps/common";
import { fShortenNumber, toDate } from "@ui/utils";
import { capitalCase } from "change-case";
import { ReactNode } from "react";
import { LayerProps } from "react-map-gl";
import { DataCategory } from "../../../../../../types";
import { LayerControlItem_T } from "../layer-controller";
import {
  getAirQualityDescription,
  getAirQualityStatus,
} from "./air-quality-grid-codes";
import { createPopupContainerChildren } from "./create-popup-container-children";

export interface SubscriberSubcategoryLayerDefinition {
  id: string;
  title: string;
  singularName: string;
  url?: string;
  mapType?: "hazard" | "vectorTileService" | "nationwide" | "nri" | "geojson";
  layer: FeatureServiceLayerDefinition;

  arcgisOptions?: Omit<ArcgisOptions, "url">;
  geojsonOptions?: GeoJsonSourceOptions;
  category: DataCategory;
  radius: number;

  iconColor?: string;

  hoverProps?: {
    title: (properties: any) => string;
    subtitle: (properties: any) => string;
    children?: (properties: any) => React.ReactNode;
  };
}

export interface SubscriberSubcategoryLayerDefOptions
  extends Omit<SubscriberSubcategoryLayerDefinition, "id" | "layer"> {
  layer: Partial<LayerProps>;
}

/**
 * This class is used to ensure that the mapbox `layer.source` and `layer.id` properties are always equal to the layer definition's `id` property.
 */
export class SubscriberSubcategoryLayerDef
  implements SubscriberSubcategoryLayerDefinition
{
  id: string;
  private options: SubscriberSubcategoryLayerDefOptions;
  constructor(id: string, options: SubscriberSubcategoryLayerDefOptions) {
    this.id = id;
    this.options = options;
  }
  get title(): string {
    return this.options?.title;
  }

  get iconColor(): string | undefined {
    return this.options?.iconColor;
  }

  get singularName(): string {
    return this.options?.singularName;
  }
  get url(): string | undefined {
    return this.options?.url;
  }
  get mapType():
    | "hazard"
    | "vectorTileService"
    | "nationwide"
    | "nri"
    | "geojson"
    | undefined {
    return this.options?.mapType;
  }

  get layer(): FeatureServiceLayerDefinition {
    return {
      ...this.options?.layer,
      id: this.id,
      source: this.id,
    } as FeatureServiceLayerDefinition;
  }

  get arcgisOptions(): Omit<ArcgisOptions, "url"> | undefined {
    return this.options?.arcgisOptions;
  }

  get geojsonOptions(): GeoJsonSourceOptions | undefined {
    return this.options?.geojsonOptions;
  }

  get category(): DataCategory {
    return this.options?.category;
  }

  get radius(): number {
    return this.options?.radius;
  }
  get hoverProps():
    | {
        title: (properties: any) => string;
        subtitle: (properties: any) => string;
        children?: (properties: any) => ReactNode;
      }
    | undefined {
    return this.options?.hoverProps;
  }
}
/**
 * This expression is used to determine if a layer is clicked or hovered
 * @ref https://docs.mapbox.com/style-spec/reference/expressions/#case
 */
const clickedOrHoveredLayerExpression = [
  "any",
  ["boolean", ["feature-state", "hover"], false],
  ["boolean", ["feature-state", "clicked"], false],
];

export const subscriberLayerMap = new Map<
  string,
  SubscriberSubcategoryLayerDefinition
>([
  [
    "natural-gas-pipelines",
    new SubscriberSubcategoryLayerDef("natural-gas-pipelines", {
      title: "Natural Gas Pipelines",
      singularName: "Natural Gas Pipeline",
      url: "https://services2.arcgis.com/FiaPA4ga0iQKduv3/ArcGIS/rest/services/Natural_Gas_Interstate_and_Intrastate_Pipelines_1/FeatureServer/0",
      mapType: "hazard",
      iconColor: colors.blue[500],
      layer: {
        type: "line",
        paint: {
          "line-width": 5,
          "line-color": [
            "case",
            ["boolean", ["feature-state", "hover"], false],
            colors.blue[800],
            colors.blue[500],
          ],
          "line-opacity": [
            "case",
            ["boolean", ["feature-state", "hover"], false],
            1,
            0.75,
          ],
        },
      },
      category: "industrial",
      radius: 5,
      hoverProps: {
        title: (props) => `Pipeline #${props["FID"]}`,
        subtitle: (props) => `Operated by ${props["Operator"]}`,
      },
    }),
  ],
  [
    "high-voltage-lines",
    new SubscriberSubcategoryLayerDef("high-voltage-lines", {
      title: "High Voltage Lines",
      singularName: "High Voltage Line",
      url: "https://services2.arcgis.com/FiaPA4ga0iQKduv3/ArcGIS/rest/services/US_Electric_Power_Transmission_Lines/FeatureServer/0",
      mapType: "hazard",
      iconColor: colors.orange[500],
      layer: {
        type: "line",
        paint: {
          "line-width": 5,
          "line-color": [
            "case",
            ["boolean", ["feature-state", "hover"], false],
            colors.orange[800],
            colors.orange[500],
          ],
          "line-opacity": [
            "case",
            ["boolean", ["feature-state", "hover"], false],
            1,
            0.75,
          ],
        },
        layout: {
          visibility: "none",
        },
      },
      category: "infrastructure",
      radius: 5,
      hoverProps: {
        title: (props) => `Line #${props["FID"] ?? "N/A"}`,
        subtitle: (props) =>
          props.OWNER === "N/A"
            ? props.VOLT_CLASS === "N/A"
              ? `Type ${props.TYPE}`
              : `Voltage Class of ${props.VOLT_CLASS} (in kv)`
            : `Owned by ${capitalCase(props.OWNER)}`,
      },
    }),
  ],
  [
    "historic-tornadoes",
    new SubscriberSubcategoryLayerDef("historic-tornadoes", {
      title: "Past Tornadoes",
      singularName: "Past Tornado",
      url: "https://services2.arcgis.com/FiaPA4ga0iQKduv3/arcgis/rest/services/Tornado_Tracks_1950_2017_1/FeatureServer/0",
      mapType: "hazard",
      iconColor: colors.green[500],
      layer: {
        type: "line",
        paint: {
          "line-width": 5,
          "line-color": [
            "case",
            ["boolean", ["feature-state", "hover"], false],
            colors.green[800],
            colors.green[500],
          ],
          "line-opacity": [
            "case",
            ["boolean", ["feature-state", "hover"], false],
            1,
            0.75,
          ],
        },
        layout: {
          visibility: "none",
        },
      },
      category: "natural",
      radius: 25,
      arcgisOptions: {
        outFields: "mo,dy,yr,len,wid",
      },
      hoverProps: {
        title: (props) =>
          `Historic Tornado – ${props.mo}/${props.dy}/${props.yr}`,
        subtitle: (props) =>
          `Length ${props.len} miles | Width ${props.wid} yards`,
        children: (props) =>
          createPopupContainerChildren([
            {
              label: "Intensity",
              value:
                props.mag === 5
                  ? "Incredible Damage"
                  : props?.mag === 4
                  ? "Devastating Damage"
                  : props?.mag === 3
                  ? "Severe Damage"
                  : props?.mag === 2
                  ? "Considerable Damage"
                  : props?.map === 1
                  ? "Minor Damage"
                  : "Light Damage",
            },
          ]),
      },
    }),
  ],
  [
    "brownfields",
    new SubscriberSubcategoryLayerDef("brownfields", {
      title: "Brownfields",
      singularName: "Brownfield Site",
      url: "https://geodata.epa.gov/arcgis/rest/services/OEI/FRS_INTERESTS/MapServer/0/",
      mapType: "hazard",
      layer: {
        type: "symbol",
        layout: {
          "icon-image": "brownfields",
          "icon-size": 1,
        },
      },
      category: "industrial",
      radius: 5,
      hoverProps: {
        title: (props) => props["PRIMARY_NAME"],
        subtitle: (props) => props["LOCATION_ADDRESS"],
      },
    }),
  ],
  [
    "superfunds",
    new SubscriberSubcategoryLayerDef("superfunds", {
      title: "Superfunds",
      singularName: "Superfund",
      url: "https://services.arcgis.com/cJ9YHowT8TU7DUyn/ArcGIS/rest/services/Superfund_National_Priorities_List_(NPL)_Sites_with_Status_Information/FeatureServer/0",
      mapType: "hazard",
      layer: {
        type: "symbol",
        layout: {
          "icon-image": "superfunds",
          "icon-size": 1,
          visibility: "none",
        },
      },
      category: "industrial",
      radius: 5,
      hoverProps: {
        title: (props) => props["Site_Name"],
        subtitle: (props) =>
          `${props?.City}, ${props?.County}, ${props?.State}`,
      },
    }),
  ],
  //  @reason Removed due to deletion of source
  //  @linear [ARE-2325](https://linear.app/areahub/issue/ARE-2325/update-premium-interactive-map-to-remove-solid-waste-landfills)
  //  @linear [ARE-2297](https://linear.app/areahub/issue/ARE-2297/inquiry-on-solid-waste-landfills-data-source)
  // [
  //   "solid-waste-landfills",
  //   new SubscriberSubcategoryLayerDef("solid-waste-landfills", {
  //     title: "Solid Waste Landfills",
  //     singularName: "Solid Waste Landfill",
  //     url: "https://services1.arcgis.com/Hp6G80Pky0om7QvQ/arcgis/rest/services/Solid_Waste_Landfill_Facilities/FeatureServer/0",
  //     mapType: "hazard",
  //     layer: {
  //       type: "symbol",
  //       layout: {
  //         "icon-image": "landfill",
  //         "icon-size": 1,
  //         visibility: "none",
  //       },
  //     },
  //     category: "infrastructure",
  //     radius: 5,
  //     hoverProps: {
  //       title: (props) => props.NAME,
  //       subtitle: (props) =>
  //         `${props?.ADDRESS} ${props?.CITY}, ${props?.STATE} ${props?.ZIP}`,
  //     },
  //   }),
  // ],
  //  @reason Removed due to deletion of source
  //  @linear [ARE-2821](https://linear.app/areahub/issue/ARE-2821)
  //  @linear [ARE-2891](https://linear.app/areahub/issue/ARE-2891)
  // [
  //   "wells",
  //   new SubscriberSubcategoryLayerDef("wells", {
  //     title: "Oil, Gas, & Fracking Wells",
  //     singularName: "Oil / Gas / Fracking Well",
  //     url: "https://services1.arcgis.com/Hp6G80Pky0om7QvQ/arcgis/rest/services/Oil_and_Natural_Gas_Wells/FeatureServer/0",
  //     mapType: "hazard",
  //     layer: {
  //       type: "symbol",
  //       layout: {
  //         "icon-image": "wells",
  //         "icon-size": 1,
  //       },
  //     },
  //     category: "industrial",
  //     radius: 5,
  //     hoverProps: {
  //       title: (props) => props?.NAME,
  //       subtitle: (props) => "Operator: " + props?.OPERATOR,
  //     },
  //   }),
  // ],
  [
    "power-plants",
    new SubscriberSubcategoryLayerDef("power-plants", {
      title: "Power Plants",
      singularName: "Power Plant",
      url: "https://services2.arcgis.com/FiaPA4ga0iQKduv3/ArcGIS/rest/services/Power_Plants_in_the_US/FeatureServer/0",
      mapType: "hazard",
      layer: {
        type: "symbol",
        layout: {
          "icon-image": "power-plant",
          "icon-size": 1,
          visibility: "none",
        },
      },
      category: "infrastructure",
      radius: 5,
      hoverProps: {
        title: (props) => props["Plant_Name"] + ` (${props.Plant_Code})`,
        subtitle: (props) =>
          "Primary Source: " + capitalCase(props?.PrimSource),
      },
    }),
  ],
  //  @reason Removed due to deletion of source
  //  @linear [ARE-2801](https://linear.app/areahub/issue/ARE-2801)
  //  @linear [ARE-2794](https://linear.app/areahub/issue/ARE-2794)
  // [
  //   "cell-towers",
  //   new SubscriberSubcategoryLayerDef("cell-towers", {
  //     title: "Cell Towers",
  //     singularName: "Cell Tower",
  //     url: "https://services1.arcgis.com/Hp6G80Pky0om7QvQ/ArcGIS/rest/services/Cellular_Towers_New/FeatureServer/0",
  //     mapType: "hazard",
  //     layer: {
  //       type: "symbol",
  //       layout: {
  //         "icon-image": "cell-towers",
  //         "icon-size": 1,
  //         visibility: "none",
  //       },
  //     },
  //     category: "infrastructure",
  //     radius: 5,
  //     hoverProps: {
  //       title: (props) =>
  //         props?.TowReg
  //           ? `Tower ${props?.TowReg}`
  //           : `Call Sign ${props?.Callsign}`,
  //       subtitle: (props) => `Licensed by ${props.Licensee}`,
  //     },
  //   }),
  // ],
  [
    "historic-wildfires-perimeters",
    new SubscriberSubcategoryLayerDef("historic-wildfires-perimeters", {
      title: "Past Wildfire Perimeters",
      singularName: "Past Wildfire Perimeter",
      url: "https://services3.arcgis.com/T4QMspbfLg3qTGWY/ArcGIS/rest/services/WFIGS_Interagency_Perimeters/FeatureServer/0",
      mapType: "hazard",
      iconColor: colors.amber[900],
      layer: {
        type: "fill",
        paint: {
          "fill-color": colors.amber[900],
          "fill-opacity": [
            "case",
            ["boolean", ["feature-state", "hover"], false],
            0.75,
            0.25,
          ],
        },
        layout: {
          visibility: "none",
        },
      },
      category: "natural",
      radius: 25,
      hoverProps: {
        title: (props) => props["irwin_IncidentName"],
        subtitle: (props) =>
          props?.["irwin_IncidentShortDescription"]
            ? props.irwin_IncidentShortDescription
            : "",
        children: (props) =>
          createPopupContainerChildren([
            {
              label: "Acres Burned",
              value: props?.["irwin_CalculatedAcres"]
                ? `Acres Burned ${fShortenNumber(props.irwin_CalculatedAcres)}`
                : props?.["poly_GISAcres"]
                ? `Acres Burned ${fShortenNumber(props.poly_GISAcres)}`
                : props?.["irwin_DailyAcres"]
                ? `Acres Burned ${fShortenNumber(props.irwin_DailyAcres)}`
                : "Unknown",
            },
            {
              label: "Started Date",
              value: toDate(props?.["irwin_CreatedOnDateTime_dt"]),
            },
            {
              label: "Estimated Cost To Date",
              value: props?.irwin_EstimatedCostToDate
                ? `${
                    props.irwin_EstimatedCostToDate > 1
                      ? "$" + fShortenNumber(props.irwin_EstimatedCostToDate)
                      : "Unknown"
                  }`
                : "Unknown",
            },
          ]),
      },
    }),
  ],
  [
    "current-wildfires-perimeters",
    new SubscriberSubcategoryLayerDef("current-wildfires-perimeters", {
      title: "Current Wildfire Perimeters",
      singularName: "Current Wildfire Perimeter",
      url: "https://services9.arcgis.com/RHVPKKiFTONKtxq3/ArcGIS/rest/services/USA_Wildfires_v1/FeatureServer/1",
      mapType: "hazard",
      iconColor: colors.red[500],
      layer: {
        type: "fill",
        paint: {
          "fill-color": colors.red[500],
          "fill-opacity": [
            "case",
            ["boolean", ["feature-state", "hover"], false],
            0.75,
            0.25,
          ],
        },
        layout: {
          visibility: "none",
        },
      },
      category: "natural",
      radius: 25,
      hoverProps: {
        title: (props) => props["IncidentName"],
        subtitle: (props) => (props?.["Comments"] ? props.Comments : ""),
        children: (props) =>
          createPopupContainerChildren([
            {
              label: "Acres Burned",
              value: props?.["GISAcres"]
                ? `Acres Burned ${fShortenNumber(props.GISAcres)}`
                : "Unknown",
            },
            {
              label: "Started Date",
              value: toDate(props?.["CreateDate"]),
            },
          ]),
      },
    }),
  ],
  [
    "historic-fires-incidents",
    new SubscriberSubcategoryLayerDef("historic-fires-incidents", {
      title: "Past Fire Incidents",
      singularName: "Past Fire Incident",
      url: "https://services3.arcgis.com/T4QMspbfLg3qTGWY/ArcGIS/rest/services/InFORM_FireOccurrence_Public/FeatureServer/0",
      mapType: "hazard",
      layer: {
        type: "symbol",
        layout: {
          "icon-image": "historic-wildfire-incident",
          "icon-size": 1,
          visibility: "none",
        },
      },
      category: "natural",
      radius: 25,
      hoverProps: {
        title: (props) => props["IncidentName"],
        subtitle: (props) =>
          props?.["IncidentShortDescription"]
            ? props.IncidentShortDescription
            : undefined,
        children: (props) => {
          const wf = new WildfireDataFormatter(props, {
            incidentName: "IncidentName",
            description: "IncidentShortDescription",
            acres: ["FinalAcres", "CalculatedAcres", "DailyAcres"],
            containmentDate: "ContainmentDateTime",
            estimatedCostToDate: "EstimatedCostToDate",
            incidentTypeCategory: "IncidentTypeCategory",
            fireCause: "FireCause",
            controlDate: "ControlDateTime",
            createDate: "CreatedOnDateTime",
            percentContained: "PercentContained",
          });
          return createPopupContainerChildren(wf.details);
        },
      },
    }),
  ],
  [
    "current-fires-incidents",
    new SubscriberSubcategoryLayerDef("current-fires-incidents", {
      title: "Current Fire Incidents",
      singularName: "Current Fire Incident",
      url: "https://services9.arcgis.com/RHVPKKiFTONKtxq3/ArcGIS/rest/services/USA_Wildfires_v1/FeatureServer/0",
      mapType: "hazard",
      layer: {
        type: "symbol",
        layout: {
          "icon-image": "wildfire-incident",
          "icon-size": 1,
          visibility: "none",
        },
      },
      category: "natural",
      radius: 25,
      hoverProps: {
        title: (props) => props["IncidentName"],
        subtitle: (props) =>
          props?.["FireDiscoveryAge"]
            ? `${props?.FireDiscoveryAge} ${
                props?.FireDiscoveryAge > 1 ? "days" : "day"
              } old`
            : "",
        children: (props) => {
          const wf = new WildfireDataFormatter(props, {
            incidentName: "IncidentName",
            description: "IncidentShortDescription",
            acres: ["FinalAcres", "CalculatedAcres", "DailyAcres"],
            containmentDate: "ContainmentDateTime",
            incidentTypeCategory: "IncidentTypeCategory",
            fireCause: "FireCause",
            controlDate: "ControlDateTime",
            createDate: "FireOutDateTime",
            percentContained: "PercentContained",
          });
          return createPopupContainerChildren(wf.details);
        },
      },
    }),
  ],
  [
    "hail",
    new SubscriberSubcategoryLayerDef("hail", {
      title: "Past Hail Storms",
      singularName: "Past Hail Storm",
      url: "https://services2.arcgis.com/FiaPA4ga0iQKduv3/arcgis/rest/services/Hailstorms_in_the_US_View/FeatureServer/0",
      mapType: "hazard",
      layer: {
        type: "symbol",
        layout: {
          "icon-image": "hail",
          "icon-size": 1,
          visibility: "none",
        },
      },
      category: "natural",
      radius: 25,
      hoverProps: {
        title: (props) => `Hail Storm ${props.date}`,
        subtitle: (props) =>
          props.mag > 3
            ? "Super Hailstorm >3 inches"
            : props?.mag > 1.5
            ? "Destructive >1.5 inches"
            : props?.mag > 1
            ? "Severe >1 inch"
            : props?.mag > 0.75
            ? "Significant >.75 inch"
            : "Potentially Damaging <.74 inch",
      },
    }),
  ],

  [
    "air-quality",
    new SubscriberSubcategoryLayerDef("air-quality", {
      title: "Current Air Quality",
      singularName: "Latest Air Quality Conditions",
      url: "https://services.arcgis.com/cJ9YHowT8TU7DUyn/ArcGIS/rest/services/AirNowLatestContoursCombined/FeatureServer/0",
      mapType: "hazard",
      layer: {
        type: "fill",
        paint: {
          "fill-color": {
            property: "gridcode",
            stops: [
              [1, "rgb(0, 228, 0)"],
              [2, "#eab308"],
              [3, "rgb(255, 126, 0)"],
              [4, "#ef4444"],
              [5, "rgb(143, 63, 151)"],
              [6, "rgb(126, 0, 35)"],
            ],
          },
          "fill-opacity": [
            "case",
            ["boolean", ["feature-state", "hover"], false],
            0.75,
            0.25,
          ],
        },
        layout: {
          visibility: "none",
        },
      },
      geojsonOptions: {
        maxzoom: 22,
      },
      arcgisOptions: {
        useServiceBounds: false,
        projectionEndpoint:
          "https://tasks.arcgisonline.com/arcgis/rest/services/Geometry/GeometryServer/project?inSR=3857&outSR=4326&geometries=-25211960.1966142%2C-3918578.23405785%2C+4948494.95561417%2C15051844.6196579&transformation=&transformForward=true&vertical=false&f=pjson",
      },
      category: "pollution",
      radius: 0,
      hoverProps: {
        title: (props) =>
          getAirQualityStatus(props.gridcode as number) as string,
        subtitle: (props) =>
          getAirQualityDescription(props.gridcode as number) as string,
      },
    }),
  ],
  [
    "historic-earthquakes",
    new SubscriberSubcategoryLayerDef("historic-earthquakes", {
      title: "Past Earthquakes",
      singularName: "Past Earthquake Epicenter",
      url: "https://services2.arcgis.com/zr3KAIbsRSUyARHG/ArcGIS/rest/services/HistoricEarthquakes/FeatureServer/0",
      mapType: "hazard",
      iconColor: colors.indigo[500],
      layer: {
        type: "circle",
        paint: {
          "circle-color": colors.indigo[500],
          "circle-radius": {
            property: "M",
            stops: [
              [1, 5],
              [2, 10],
              [3, 20],
              [4, 30],
              [5, 40],
              [6, 50],
              [7, 60],
            ],
          },
          "circle-opacity": [
            "case",
            ["boolean", ["feature-state", "hover"], false],
            0.75,
            0.25,
          ],
        },
        layout: {
          visibility: "none",
        },
      },
      geojsonOptions: {
        maxzoom: 22,
      },
      category: "natural",
      radius: 25,
      hoverProps: {
        title: (props) => `Magnitude ${props.M} Earthquake`,
        subtitle: (props) =>
          `Recorded on ${props.month}-${props.day}-${props.year}`,
      },
    }),
  ],
  [
    "hurricanes",
    new SubscriberSubcategoryLayerDef("hurricanes", {
      title: "Hurricanes",
      singularName: "Hurricane",
      url: "https://services2.arcgis.com/FiaPA4ga0iQKduv3/ArcGIS/rest/services/IBTrACS_ALL_list_v04r00_lines_1/FeatureServer/0",
      mapType: "hazard",
      iconColor: colors.purple[500],
      layer: {
        type: "line",
        paint: {
          "line-color": colors.purple[500],
          "line-width": 10,
          "line-opacity": [
            "case",
            ["boolean", ["feature-state", "hover"], false],
            0.75,
            0.25,
          ],
        },
        layout: {
          visibility: "none",
        },
      },
      arcgisOptions: {
        where: "NAME not in ('NOT_NAMED')",
        outFields: "USA_PRES, USA_WIND, month, day, year, NAME",
      },
      geojsonOptions: {
        maxzoom: 22,
      },
      category: "natural",
      radius: 150,
      hoverProps: {
        title: (props) => props.NAME,
        subtitle: (props) =>
          `Recorded on ${props.month}-${props.day}-${props.year}`,
        children: (props) =>
          createPopupContainerChildren([
            {
              label: "Max Wind Speed",
              value: props?.USA_WIND + " knots",
            },
            {
              label: "Max Pressure",
              value: props?.USA_PRES ? props?.USA_PRES + " mb" : "Unknown",
            },
          ]),
      },
    }),
  ],
  [
    "toxic-releases",
    new SubscriberSubcategoryLayerDef("toxic-releases", {
      title: "Toxic Release Inventory Sites",
      singularName: "Toxic Release Inventory Site",
      url: "https://services.arcgis.com/cJ9YHowT8TU7DUyn/arcgis/rest/services/FRS_INTERESTS_TRI/FeatureServer/0",
      mapType: "hazard",
      layer: {
        type: "symbol",
        layout: {
          "icon-image": "toxic-releases",
          "icon-size": 1,
          visibility: "none",
        },
      },
      arcgisOptions: {
        useServiceBounds: false,
      },
      category: "pollution",
      radius: 5,
      hoverProps: {
        title: (props) => props.PRIMARY_NAME,
        subtitle: (props) =>
          `${props?.LOCATION_ADDRESS} ${props?.CITY_NAME}, ${props?.STATE_CODE} ${props?.POSTAL_CODE}`,
      },
    }),
  ],
  [
    "parks",
    new SubscriberSubcategoryLayerDef("parks", {
      title: "Parks",
      singularName: "Park",
      url: "https://services.arcgis.com/cJ9YHowT8TU7DUyn/arcgis/rest/services/FRS_INTERESTS_TRI/FeatureServer/0",
      mapType: "hazard",
      iconColor: "#064e3b",
      layer: {
        type: "fill",
        paint: {
          "fill-color": "#064e3b",
          "fill-opacity": ["case", clickedOrHoveredLayerExpression, 0.75, 0.5],
          "fill-outline-color": "#000000",
        },
      },
      category: "other",
      radius: 5,
      hoverProps: {
        title: (props) => props.NAME,
        subtitle: (props) => props?.FEATTYPE,
      },
    }),
  ],
]);

export const subcategoryLayers = Array.from(subscriberLayerMap.values());

const filterByCategory = (category: DataCategory) =>
  subcategoryLayers.filter((layer) => layer.category === category);

const toLayerControlItem = (
  layer: SubscriberSubcategoryLayerDefinition,
): LayerControlItem_T => ({
  id: layer.id,
  title: layer.title,
});

export const defaultLayerControllerMap = new Map<
  DataCategory,
  LayerControlItem_T[]
>(
  ["industrial", "infrastructure", "natural", "pollution", "other"].map(
    (category) => {
      return [
        category as DataCategory,
        filterByCategory(category as DataCategory).map(
          toLayerControlItem,
        ) as LayerControlItem_T[],
      ];
    },
  ),
);
